import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { Box, Paper, Drawer } from "@mui/material";
import { ArrowRight } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { ViewPayCycleIncentive } from "./ViewPayCycleIncentive";
import { useFetch } from "services/hooks/useFetch";
import { checkViewerPermission } from "services/utils/HelperFunction";
import {
  themes,
  scrollTop,
  stickyHeader,
  tableRowSize,
  StatusBadge,
  displayFlexItem,
  drawerStyle,
  tableRowSx,
} from "services/constants";
import { incentiveHeaders } from "pages/PayRoll/PayRollHeaders";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import moment from "moment";

export const Completed = () => {
  const { domain, topbarCompanyId, userScreenAccess, topbarCompanyName } =
    useSelector((state) => state.tokenReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filteredRecords, setFilteredRecords] = useState([]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedRowData, setExpandedRowData] = useState("");

  const containerRef = useRef(null);

  const {
    result: records,
    setLoad: reCallApi,
    loading,
  } = useFetch(
    `${domain}payroll/incentive/incentive-crud/?status=Completed&company_id=${topbarCompanyId}`, // ✅ updated API
  );

  // Was checking ("Approve", "Incentive") — a copy-paste leftover from a
  // TeamZone Approve screen (the "✅ updated module name" comment was
  // wrong; "Approve" isn't a Payroll module at all). This screen lives
  // under Payroll Processing, so it checks that module's own submodule
  // instead, same as its sibling AwaitingApproval.js.
  const hasAccess = checkViewerPermission(
    userScreenAccess?.module_details,
    "Payroll Processing",
    "Payroll Processing",
  );

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (data) => {
      setFilteredRecords(data);
      if (page * rowsPerPage >= data.length) {
        setPage(0);
      }
    },
    [page, rowsPerPage],
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [topbarCompanyId, reCallApi]);

  const handleToggle = (index, value) => {
    if (value?.id !== expandedRowData) {
      setExpandedIndex((prev) => (prev === index ? null : index));
      setExpandedRowData(value?.id);
    } else {
      setExpandedIndex(null);
      setExpandedRowData(0);
    }
  };

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [topbarCompanyName],
  );

  if (loading) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      <Box sx={{ width: "100%" }}>
        <Paper
          sx={{ height: "78vh", display: "flex", flexDirection: "column" }}
        >
          <CustomFilter
            title="Completed Incentives"
            filterHeaders={incentiveHeaders}
            data={records}
            filteredData={filteredRecords}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={hasAccess}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            // newButtonComponent={NewButton}
          />
          <Mui.TableContainer
            ref={containerRef}
            sx={{ maxHeight: "60vh", paddingLeft: 1.5, paddingRight: 1.5 }}
          >
            <Mui.Table size="small" sx={{ minWidth: 900 }}>
              {" "}
              <Mui.TableHead sx={stickyHeader}>
                {" "}
                <Mui.TableRow sx={tableRowSize}>
                  {" "}
                  {incentiveHeaders.map((h) => (
                    <Mui.TableCell
                      key={h.key}
                      sx={{ fontWeight: "bold", color: themes.headLine }}
                    >
                      {" "}
                      {h.name}{" "}
                    </Mui.TableCell>
                  ))}{" "}
                </Mui.TableRow>{" "}
              </Mui.TableHead>{" "}
              <Mui.TableBody>
                {" "}
                {filteredRecords.length === 0 ? (
                  <TableEmptyState dataLength={records} />
                ) : (
                  filteredRecords
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <React.Fragment key={index}>
                        {" "}
                        <Mui.TableRow>
                          {" "}
                          {incentiveHeaders.map((head) =>
                            head.key === "expand" ? (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => handleToggle(index, row)}
                              >
                                {" "}
                                <ArrowRight
                                  sx={{
                                    transform:
                                      expandedIndex === index
                                        ? "rotate(90deg)"
                                        : "rotate(0deg)",
                                  }}
                                />{" "}
                              </Mui.TableCell>
                            ) : (
                              <Mui.TableCell
                                key={head.key}
                                onClick={() => {
                                  setSelectedRecord(row);
                                  setIsEditOpen(true);
                                }}
                              >
                                {" "}
                                {head.key === "status" ? (
                                  <Mui.Box sx={displayFlexItem}>
                                    {" "}
                                    <StatusBadge
                                      color={row?.color_code}
                                      label={row?.status}
                                    />{" "}
                                  </Mui.Box>
                                ) : (
                                  (row[head.key] ?? "N/A")
                                )}{" "}
                              </Mui.TableCell>
                            ),
                          )}{" "}
                        </Mui.TableRow>{" "}
                        {expandedRowData === row?.id ? (
                          <Mui.TableRow sx={tableRowSx}>
                            <Mui.TableCell
                              style={{ paddingBottom: 0, paddingTop: 0 }}
                              colSpan={10}
                            >
                              <Mui.Collapse
                                in={true}
                                timeout="auto"
                                unmountOnExit
                              >
                                {expandedRowData === row?.id ? (
                                  <Mui.Box
                                    textAlign="center"
                                    sx={{
                                      margin: 2,
                                    }}
                                  >
                                    {expandedRowData === row?.id &&
                                    row?.qualified_users?.lenght === 0 ? (
                                      <Mui.Alert severity="info">
                                        No members have been assigned under this
                                        team.
                                      </Mui.Alert>
                                    ) : (
                                      expandedRowData === row?.id && (
                                        <Mui.Table>
                                          <Mui.TableHead
                                            sx={{
                                              backgroundColor: themes.primary,
                                            }}
                                          >
                                            <Mui.TableRow sx={tableRowSx}>
                                              <Mui.TableCell
                                                sx={{
                                                  color: themes.headLine,
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Employee ID
                                              </Mui.TableCell>

                                              <Mui.TableCell
                                                sx={{
                                                  color: themes.headLine,
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Employee Name
                                              </Mui.TableCell>
                                              <Mui.TableCell
                                                sx={{
                                                  color: themes.headLine,
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                Amount
                                              </Mui.TableCell>
                                            </Mui.TableRow>
                                          </Mui.TableHead>
                                          {expandedRowData === row?.id &&
                                            row?.qualified_users?.map(
                                              (member, index) => (
                                                <Mui.TableBody key={index}>
                                                  <Mui.TableRow sx={tableRowSx}>
                                                    <Mui.TableCell>
                                                      {member.employee_id}
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      <Mui.Stack direction="Row">
                                                        <Mui.Avatar
                                                          sx={{
                                                            width: 25,
                                                            height: 25,
                                                          }}
                                                          alt="Remy Sharp"
                                                          src={member.image_url}
                                                        />
                                                        <Mui.Typography
                                                          sx={{
                                                            paddingLeft: 1,
                                                          }}
                                                        >
                                                          {member.name}
                                                        </Mui.Typography>
                                                      </Mui.Stack>
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {member.total_amount
                                                        ? member.total_amount
                                                        : "0"}
                                                    </Mui.TableCell>
                                                  </Mui.TableRow>
                                                </Mui.TableBody>
                                              ),
                                            )}
                                        </Mui.Table>
                                      )
                                    )}
                                  </Mui.Box>
                                ) : null}
                              </Mui.Collapse>
                            </Mui.TableCell>
                          </Mui.TableRow>
                        ) : null}
                      </React.Fragment>
                    ))
                )}{" "}
              </Mui.TableBody>{" "}
            </Mui.Table>
          </Mui.TableContainer>
          {filteredRecords.length > 25 && (
            <Mui.TablePagination
              component="div"
              rowsPerPageOptions={[25, 50, 75, 100]}
              count={filteredRecords.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}{" "}
        </Paper>
      </Box>

      <Drawer
        anchor="right"
        open={isCreateOpen || isEditOpen}
        onClose={() =>
          isCreateOpen ? setIsCreateOpen(false) : setIsEditOpen(false)
        }
        sx={drawerStyle}
      >
        {isEditOpen && (
          <ViewPayCycleIncentive
            selectedRecord={selectedRecord}
            onClose={() => setIsEditOpen(false)}
            reCallApi={reCallApi}
            screen="CompletedScreen"
          />
        )}
      </Drawer>
    </React.Fragment>
  );
};
